import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	OnInit,
	Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import { BusinessUser } from '@models/businesses';
import { FinancialInstitution } from '@models/lender';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
	CloserSyncService,
	ICloserSyncPinForm,
} from '@services/closer-sync/closer-sync.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { GlobalFinancialInstitutionsState } from '@state/global-financial-institutions/global-financial-institutions.state';
import LogRocket from 'logrocket';
import {
	CloserSyncInstructionsAPIService,
	ValidateVerificationCodeModel,
} from 'src/app/shared/api-repos/mortgages/closer-sync-instructions.api.service';
import { LSOEmailExists } from 'src/app/shared/validators/lso-email-exists.validator';

enum ConnectSteps {
	WELCOME = 'welcome',
	EMAIL = 'email',
	TERMS_OF_SERVICE = 'termsOfService',
	VERIFICATION_CODE = 'verificationCode',
	VERIFY = 'verify',
	SIGNATURE = 'signature',
	PIN = 'pin',
	LENDER_CONSENT = 'lenderConsent',
	CONGRATS_CONNECTED = 'congratsConnected',
}

type ICurrentStep = Record<
	ConnectSteps,
	{
		stepNum?: number;
		nextAction?: VoidFunction;
		backAction?: VoidFunction;
		nextActionText?: string;
		backActionText?: string;
		loadingText?: string;
	}
>;

@UntilDestroy()
@Component({
	selector: 'app-connect-main-modal',
	templateUrl: './connect-main-modal.component.html',
	styleUrls: ['./connect-main-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ConnectMainModalComponent implements OnInit {
	_connectStepsEmum = ConnectSteps;
	_connectSteps: ICurrentStep = {
		[ConnectSteps.WELCOME]: {
			nextAction: () => this.onContinueClick(),
			nextActionText: 'Continue',
			loadingText: "Let's get you Synced...",
		},
		[ConnectSteps.EMAIL]: {
			stepNum: 1,
			backAction: () => {
				this.currentStep = this._connectSteps[ConnectSteps.WELCOME];
			},
			nextAction: async () => this.validateEmailClick(),
			nextActionText: 'Send',
			loadingText: 'Sending you an email with verification code...',
		},
		[ConnectSteps.TERMS_OF_SERVICE]: {
			backAction: () => {
				this.currentStep = this._connectSteps[ConnectSteps.EMAIL];
			},
		},
		[ConnectSteps.VERIFICATION_CODE]: {
			stepNum: 2,
			nextAction: () => this.validateCodeClick(),
			backAction: () => {
				this.currentStep = this._connectSteps[ConnectSteps.EMAIL];
			},
			nextActionText: 'Connect',
			loadingText: 'Please wait while we send a verification code...',
		},
		[ConnectSteps.VERIFY]: {
			nextActionText: 'Verify',
			loadingText: 'Verifying your account...',
		},
		[ConnectSteps.SIGNATURE]: {
			stepNum: 3,
			nextAction: () => this.saveSignature(),
			backAction: () => {
				this.currentStep = this._connectSteps[ConnectSteps.EMAIL];
			},
			loadingText: 'Signing your account...',
		},
		[ConnectSteps.PIN]: {
			stepNum: 4,
			nextAction: async () => await this.savePin(),
			backAction: () => {
				this.currentStep = this._connectSteps[ConnectSteps.SIGNATURE];
			},
			loadingText: 'Encrypting your access PIN...',
		},
		[ConnectSteps.LENDER_CONSENT]: {
			stepNum: 5,
			nextActionText: 'Continue',
			nextAction: () => this.saveLenderConsent(),
			backAction: () => {
				this.currentStep = this._connectSteps[ConnectSteps.PIN];
			},
			loadingText: 'Verifying your account...',
		},
		[ConnectSteps.CONGRATS_CONNECTED]: {
			nextActionText: 'Continue',
			backActionText: 'Close',
			backAction: () => this.close(false),
			nextAction: () => this.close(true),
		},
	};

	@Output() connectComplete: EventEmitter<string> = new EventEmitter<string>();
	@SelectSnapshot(BusinessUsersState.getLoggedInBusinessUser)
	loggedInBusinessUser: BusinessUser;
	@SelectSnapshot(GlobalFinancialInstitutionsState.getSyncInstitutions)
	syncInstitutions: FinancialInstitution[];

	public ICON = ICON;

	validating = false;
	pinVerified = false;
	invalidEmail = false;
	currentStep = this._connectSteps[ConnectSteps.WELCOME];
	form: FormGroup;
	signature: string;
	numOfSteps = Math.max(
		...Object.values(this._connectSteps).map((obj) => obj.stepNum ?? 0),
	);
	closerSyncLenders = [];

	public get disableNextButton(): boolean {
		switch (this.currentStep) {
			case this._connectSteps[ConnectSteps.WELCOME]:
			case this._connectSteps[ConnectSteps.CONGRATS_CONNECTED]:
				return false;
			case this._connectSteps[ConnectSteps.EMAIL]:
				return ['email', 'termsAndConditions'].some(
					(name) => this.form.get(name).invalid,
				);
			case this._connectSteps[ConnectSteps.VERIFICATION_CODE]:
				return this.form.get('pin').invalid;
			case this._connectSteps[ConnectSteps.SIGNATURE]:
				return this.form.get('signatureConsent').invalid || !this.signature;
			case this._connectSteps[ConnectSteps.PIN]:
				return this.form.get('accessPinForm').invalid;
			case this._connectSteps[ConnectSteps.LENDER_CONSENT]:
				return (
					this.form.get('lenderConsent').invalid ||
					this.form.get('lsoConsent').invalid
				);
			default:
				return true;
		}
	}

	constructor(
		private instructionsService: CloserSyncInstructionsAPIService,
		private dialogRef: MatDialogRef<ConnectMainModalComponent>,
		private formBuilder: FormBuilder,
		private snackBarService: SnackbarService,
		private lsoEmailExists: LSOEmailExists,
		private closerSyncService: CloserSyncService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.form = this.formBuilder.group({
			email: this.formBuilder.control('', {
				validators: [Validators.email, Validators.required],
				updateOn: 'change',
			}),
			termsAndConditions: this.formBuilder.control(false, [
				Validators.requiredTrue,
			]),
			pin: this.formBuilder.control('', [Validators.required]),
			signatureConsent: this.formBuilder.control(false, [
				Validators.requiredTrue,
			]),
			lsoConsent: this.formBuilder.control(false, [Validators.requiredTrue]),
			accessPinForm: this.closerSyncService.createPinForm(),
			lenderConsent: this.formBuilder.control(false, [Validators.requiredTrue]),
		});

		if (this.loggedInBusinessUser.signature) {
			this.signature = this.loggedInBusinessUser.signature;
		}

		this.closerSyncLenders = this.syncInstitutions.map((inst) => ({
			label: inst.displayName || inst.name,
			imgUrl: inst.logoUrl,
		}));

		this.setCurrentStep();
	}

	private setCurrentStep() {
		if (!this.loggedInBusinessUser.lsoEmail) {
			this.currentStep = this._connectSteps[ConnectSteps.WELCOME];
			return;
		}

		if (!this.loggedInBusinessUser.closerSyncId) {
			this.form.get('email').setValue(this.loggedInBusinessUser.lsoEmail);
			this.currentStep = this._connectSteps[ConnectSteps.VERIFICATION_CODE];
			return;
		}

		if (!this.loggedInBusinessUser.signature) {
			this.currentStep = this._connectSteps[ConnectSteps.SIGNATURE];
			return;
		}

		if (!this.loggedInBusinessUser.accessPin) {
			this.currentStep = this._connectSteps[ConnectSteps.PIN];
			return;
		}

		if (!this.loggedInBusinessUser.lenderConsent) {
			this.currentStep = this._connectSteps[ConnectSteps.LENDER_CONSENT];
			return;
		}
	}

	close(hasCompletedAuthFlow?: boolean): void {
		this.dialogRef.close(hasCompletedAuthFlow);
	}

	public onContinueClick(): void {
		this.currentStep = this._connectSteps[ConnectSteps.EMAIL];
	}

	public async validateEmailClick() {
		this.validating = true;
		const emailControl = this.form.get('email');
		const emailExistsError = await this.lsoEmailExists
			.validate(emailControl)
			.toPromise();

		if (emailExistsError) {
			emailControl.setErrors(emailExistsError);
			this.validating = false;
			this.cdr.detectChanges();
			return;
		}

		try {
			this.validating = true;
			await this.instructionsService
				.setLsoEmail(this.form.get('email').getRawValue().toLowerCase())
				.toPromise();
			this.snackBarService.openSnackBar({
				text: 'Email has been sent. Please check your inbox for your verification code.',
				type: 'success',
			});
			this.currentStep = this._connectSteps[ConnectSteps.VERIFICATION_CODE];

			this.invalidEmail = false;
		} catch (error) {
			this.invalidEmail = true;
			console.log('error', error?.status);
			LogRocket.captureException(error);
		} finally {
			this.validating = false;
			this.cdr.detectChanges();
		}
	}

	public validateCodeClick() {
		this.validating = true;

		const validateVerficationCodeValue: ValidateVerificationCodeModel = {
			email: this.form.get('email').getRawValue().toLowerCase().trim(),
			pin: this.form.get('pin').getRawValue(),
		};

		this.instructionsService
			.connect(this.loggedInBusinessUser.id, validateVerficationCodeValue)
			.pipe(untilDestroyed(this))
			.toPromise()
			.then(() => {
				this.pinVerified = true;
				this.snackBarService.openSnackBar({
					text: 'Verification Code Correct. Your email has been successfully verified!',
					type: 'success',
				});
				this.currentStep = this._connectSteps[ConnectSteps.SIGNATURE];
			})
			.catch((e: HttpErrorResponse) => {
				if (e.status === 401) {
					LogRocket.captureException(e);
					this.form.get('pin').setErrors({ invalidVerificationCode: true });
					this.snackBarService.openSnackBar({
						text: 'Verification Code Incorrect. Please try again or request a new verification code.',
						type: 'info',
					});
				} else {
					LogRocket.error('Error when trying to validate verification code', e);
					this.snackBarService.openSnackBar({
						text: 'Something went wrong, please try again. If the problem continues please contact support.',
						type: 'error',
					});
				}
			})
			.finally(() => {
				this.validating = false;
				this.cdr.detectChanges();
			});
	}

	public saveSignature() {
		this.validating = true;

		try {
			this.closerSyncService.saveSignature(this.signature);
			this.currentStep = this._connectSteps[ConnectSteps.PIN];
		} catch (error) {
			console.error('error', error);
		} finally {
			this.validating = false;
			this.cdr.detectChanges();
		}
	}

	public async savePin() {
		this.validating = true;

		try {
			await this.closerSyncService.savePin(
				this.form.get('accessPinForm') as FormGroup<ICloserSyncPinForm>,
			);
			this.currentStep = this._connectSteps[ConnectSteps.LENDER_CONSENT];
		} catch (error) {
			console.error('error', error);
		} finally {
			this.validating = false;
			this.cdr.detectChanges();
		}
	}

	public saveLenderConsent() {
		this.validating = true;

		try {
			this.closerSyncService.saveLenderConsent();
			this.currentStep = this._connectSteps[ConnectSteps.CONGRATS_CONNECTED];
		} catch (error) {
			console.error('error', error);
		} finally {
			this.validating = false;
			this.cdr.detectChanges();
		}
	}

	public termsOfServiceClick(): void {
		this.currentStep = this._connectSteps[ConnectSteps.TERMS_OF_SERVICE];
	}
}
